import { useCallback, useEffect, useRef, useState } from "react";
import { useLenis } from "./SmoothScroll";

const MIN_THUMB_HEIGHT = 76;

type ScrollbarState = {
  enabled: boolean;
  thumbHeight: number;
};

export default function GlobalScrollbar() {
  const lenis = useLenis();
  const railRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLButtonElement>(null);
  const dragOffsetRef = useRef(0);
  const [state, setState] = useState<ScrollbarState>({ enabled: false, thumbHeight: MIN_THUMB_HEIGHT });

  const updateThumbPosition = useCallback(() => {
    const rail = railRef.current;
    const thumb = thumbRef.current;
    const scrollingElement = document.scrollingElement ?? document.documentElement;
    if (!rail || !thumb) return;

    const scrollRange = Math.max(0, scrollingElement.scrollHeight - window.innerHeight);
    const currentScroll = scrollingElement.scrollTop || window.scrollY;
    const progress = scrollRange > 0 ? Math.min(1, Math.max(0, currentScroll / scrollRange)) : 0;
    const travel = Math.max(0, rail.clientHeight - thumb.offsetHeight);
    thumb.style.transform = `translate3d(0, ${Math.round(progress * travel)}px, 0)`;
    thumb.setAttribute("aria-valuenow", String(Math.round(progress * 100)));
  }, []);

  const queueThumbUpdate = useCallback(() => {
    updateThumbPosition();
  }, [updateThumbPosition]);

  const measure = useCallback(() => {
    const scrollingElement = document.scrollingElement ?? document.documentElement;
    const viewportHeight = window.innerHeight;
    const scrollRange = Math.max(0, scrollingElement.scrollHeight - viewportHeight);
    const bodyStyle = window.getComputedStyle(document.body);
    const scrollLocked = bodyStyle.overflow === "hidden" || bodyStyle.overflowY === "hidden" || bodyStyle.position === "fixed";
    const railHeight = railRef.current?.clientHeight ?? viewportHeight;
    const thumbHeight = Math.max(MIN_THUMB_HEIGHT, Math.round((viewportHeight / scrollingElement.scrollHeight) * railHeight));

    const nextState = {
      enabled: scrollRange > 1 && !scrollLocked,
      thumbHeight: Math.min(railHeight, thumbHeight),
    };
    setState((current) => current.enabled === nextState.enabled && current.thumbHeight === nextState.thumbHeight ? current : nextState);
    queueThumbUpdate();
  }, [queueThumbUpdate]);

  useEffect(() => {
    measure();
    window.addEventListener("scroll", queueThumbUpdate, { passive: true });
    window.addEventListener("resize", measure);

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(document.documentElement);
    resizeObserver.observe(document.body);

    const bodyObserver = new MutationObserver(measure);
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ["style", "class"] });

    return () => {
      window.removeEventListener("scroll", queueThumbUpdate);
      window.removeEventListener("resize", measure);
      resizeObserver.disconnect();
      bodyObserver.disconnect();
    };
  }, [measure, queueThumbUpdate]);

  useEffect(() => {
    queueThumbUpdate();
  }, [state.thumbHeight, queueThumbUpdate]);

  useEffect(() => {
    if (!lenis) return;
    lenis.on("scroll", queueThumbUpdate);
    queueThumbUpdate();
    return () => lenis.off("scroll", queueThumbUpdate);
  }, [lenis, queueThumbUpdate]);

  const scrollFromPointer = (clientY: number, dragOffset = state.thumbHeight / 2) => {
    const rail = railRef.current;
    const scrollingElement = document.scrollingElement ?? document.documentElement;
    if (!rail) return;

    const railRect = rail.getBoundingClientRect();
    const travel = Math.max(1, railRect.height - state.thumbHeight);
    const nextThumbTop = Math.min(travel, Math.max(0, clientY - railRect.top - dragOffset));
    const scrollRange = Math.max(0, scrollingElement.scrollHeight - window.innerHeight);
    window.scrollTo({ top: (nextThumbTop / travel) * scrollRange, behavior: "auto" });
  };

  const handleThumbPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    dragOffsetRef.current = event.clientY - rect.top;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleThumbPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    scrollFromPointer(event.clientY, dragOffsetRef.current);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const pageStep = Math.max(120, window.innerHeight * 0.82);
    if (event.key === "ArrowDown") window.scrollBy({ top: 80, behavior: "smooth" });
    else if (event.key === "ArrowUp") window.scrollBy({ top: -80, behavior: "smooth" });
    else if (event.key === "PageDown") window.scrollBy({ top: pageStep, behavior: "smooth" });
    else if (event.key === "PageUp") window.scrollBy({ top: -pageStep, behavior: "smooth" });
    else if (event.key === "Home") window.scrollTo({ top: 0, behavior: "smooth" });
    else if (event.key === "End") window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
    else return;
    event.preventDefault();
  };

  return (
    <aside className={`ez-scrollbar ${state.enabled ? "is-visible" : ""}`} aria-hidden={!state.enabled}>
      <div
        ref={railRef}
        className="ez-scrollbar__rail"
        onPointerDown={(event) => {
          if ((event.target as HTMLElement).closest(".ez-scrollbar__thumb")) return;
          scrollFromPointer(event.clientY);
        }}
      >
        <button
          ref={thumbRef}
          type="button"
          className="ez-scrollbar__thumb"
          style={{ height: state.thumbHeight, transform: "translate3d(0, 0, 0)" }}
          role="scrollbar"
          aria-label="Page scroll"
          aria-orientation="vertical"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={0}
          tabIndex={state.enabled ? 0 : -1}
          onPointerDown={handleThumbPointerDown}
          onPointerMove={handleThumbPointerMove}
          onKeyDown={handleKeyDown}
        >
          <span className="ez-scrollbar__core" />
        </button>
      </div>
    </aside>
  );
}
